/* eslint-disable react/display-name */
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useFlag } from '@/features/unleash';
import { ViewMode } from './enum';
import { LoadingWarren, Text } from '@simplywallst/ui-core';
import { useSection } from './useSection';
import type { CompanyReportSection } from './useSection';
import { useVisibleSectionsContext } from './components/VisibleSectionsContextProvider';
import { getViewMode } from '@/redux/location/selectors';
import { useSelector } from 'react-redux';
import {
  LoadingContainer,
  LoadingWrapper,
  ReportContentWrapper,
  LoadingGrid,
  LoadingContent,
} from './styled';

interface ComponentProps {
  [key: string]: unknown;
}
interface PreloadingComponentProps extends ComponentProps {
  anchor: CompanyReportSection;
  sectionNumber?: number;
}

interface ComponentWithLoadingProps extends PreloadingComponentProps {
  Component: React.ComponentType<ComponentProps>;
}

interface MemoProps {
  enabled: boolean;
  currentSection: CompanyReportSection;
  anchor: CompanyReportSection;
}

type ComponentWithMemoProps = ComponentProps & MemoProps;

type ComponentWithLoadingMemoProps = ComponentWithLoadingProps & MemoProps;

export const arePropsEqual = (prevProps: MemoProps, nextProps: MemoProps) => {
  const { currentSection: currentSectionThis, anchor, enabled } = prevProps;
  const { currentSection: currentSectionNext } = nextProps;
  const isDifferentSection =
    enabled &&
    currentSectionThis &&
    currentSectionNext &&
    currentSectionNext !== anchor;

  // Return true to skip rendering, false to render
  return isDifferentSection;
};

const ComponentWithLoading = ({
  Component,
  anchor,
  sectionNumber,
  ...props
}: ComponentWithLoadingProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const timeoutId = useRef<NodeJS.Timeout | undefined>();
  const { currentSection } = useSection();
  const [isReadyToLoad, setReadyToLoad] = useState<boolean>(false);
  const [isContentReady, setIsContentReady] = useState<boolean>(false);
  const [isShowFull, setIsShowFull] = useState<boolean>(false);
  const { getSectionLabel } = useVisibleSectionsContext();

  const isShowLoading = !isReadyToLoad || !isContentReady || !isShowFull;

  const checkFinishLoading = useCallback(() => {
    if (currentSection === anchor) {
      const contentHeight = contentRef.current?.clientHeight || 0;
      const isFinishLoading = contentHeight > 200;
      if (!isFinishLoading) {
        clearTimeout(timeoutId.current);
        timeoutId.current = setTimeout(checkFinishLoading, 200);
      } else {
        if (!isContentReady) {
          setIsContentReady(true);
        }
      }
    }
  }, [anchor, currentSection, isContentReady]);

  useEffect(() => {
    if (currentSection === anchor && !isReadyToLoad) {
      setReadyToLoad(true);
    }
  }, [isReadyToLoad, currentSection, anchor]);

  useEffect(() => {
    if (isReadyToLoad) {
      checkFinishLoading();
    }

    return () => {
      clearTimeout(timeoutId.current);
    };
  }, [anchor, checkFinishLoading, currentSection, isReadyToLoad]);

  useEffect(() => {
    if (!isShowFull && isContentReady) {
      setIsShowFull(true);
    }
  }, [anchor, currentSection, isContentReady, isShowFull]);

  return (
    <LoadingContainer isShowLoading={isShowLoading}>
      {isShowLoading && (
        <LoadingWrapper>
          <LoadingGrid
            justifyContent="center"
            placeItems={anchor === 'summary' ? 'start' : 'center'}
          >
            <LoadingContent gap="32px">
              <LoadingWarren />
              <Text>Loading {getSectionLabel(anchor)}</Text>
            </LoadingContent>
          </LoadingGrid>
        </LoadingWrapper>
      )}
      <ReportContentWrapper ref={contentRef} isShowLoading={isShowLoading}>
        {isReadyToLoad && (
          <Component sectionNumber={sectionNumber} anchor={anchor} {...props} />
        )}
      </ReportContentWrapper>
    </LoadingContainer>
  );
};

const ComponentWithLoadingMemo = memo<ComponentWithLoadingMemoProps>(
  ComponentWithLoading,
  arePropsEqual
);

const withLoading = (Component) => {
  const MemoComponent = memo<ComponentWithMemoProps>(Component, arePropsEqual);

  return ({ anchor, sectionNumber, ...props }: PreloadingComponentProps) => {
    const flag = useFlag({ name: 'mono-loading-report' });

    const { currentSection } = useSection();
    const landedSection = useRef(currentSection);
    const isFirstSection = landedSection.current === anchor;

    const viewMode = useSelector(getViewMode);
    const isPrintMode = viewMode === ViewMode.Print;

    const loadingFlagEnabled = flag.flagsReady && flag.enabled;
    const shouldShowLoading =
      loadingFlagEnabled && !isPrintMode && !isFirstSection;

    if (!shouldShowLoading) {
      return (
        <MemoComponent
          enabled={loadingFlagEnabled}
          currentSection={currentSection}
          anchor={anchor}
          sectionNumber={sectionNumber}
          {...props}
        />
      );
    }

    return (
      <ComponentWithLoadingMemo
        enabled={loadingFlagEnabled}
        currentSection={currentSection}
        Component={Component}
        anchor={anchor}
        sectionNumber={sectionNumber}
        {...props}
      />
    );
  };
};

export default withLoading;
